import type { FC, ReactNode } from 'react'
import type { Profile } from '@/types'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { getContactLinks } from '@/lib/contact-links'
import { Container } from '@/components/generals/container'
import { LinkIcon } from '@/components/generals/link-icon'
import { SectionLabel } from '@/components/generals/section-label'
import { Star } from '@/components/generals/star'
import { ContactForm } from '@/components/landing/contact-form'
import { Reveal } from '@/components/motion/reveal'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface ContactSectionProps {
    profile: Profile | null
}

// Orange finale: the headline grows into place as it scrolls in, next to a
// working contact form.
export const ContactSection: FC<ContactSectionProps> = ({ profile }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const reduced = usePrefersReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'start 0.2'] })
    const titleScale = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [0.82, 1])
    const titleY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [90, 0])
    const starRotate = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [-120, 0])
    const links = profile ? getContactLinks(profile) : []

    return (
        <section id='contact' ref={ref} className='relative overflow-hidden bg-primary py-20 text-primary-foreground md:py-32'>
            <motion.div
                aria-hidden='true'
                style={{ rotate: starRotate }}
                className='pointer-events-none absolute -right-24 -top-24 size-[22rem] text-accent md:size-[32rem]'
            >
                <Star className='size-full' />
            </motion.div>

            <Container className='relative grid gap-12 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-6'>
                    <Reveal>
                        <SectionLabel index='05' label='Contact' />
                    </Reveal>

                    {profile?.contact_title && (
                        <motion.h2
                            style={{ scale: titleScale, y: titleY }}
                            className='mt-8 origin-bottom-left font-heading text-[clamp(2.8rem,6.5vw,5.75rem)] font-extrabold leading-[0.95] tracking-[-0.035em]'
                        >
                            <span data-mdlfy-cms={`profile:${profile.id}:contact_title`}>{profile.contact_title}</span>
                        </motion.h2>
                    )}

                    {profile?.contact_text && (
                        <Reveal delay={0.1}>
                            <p data-mdlfy-cms={`profile:${profile.id}:contact_text`} className='mt-6 max-w-lg text-lg leading-relaxed md:text-xl'>
                                {profile.contact_text}
                            </p>
                        </Reveal>
                    )}

                    {links.length > 0 && (
                        <ul className='mt-10 flex flex-col gap-3'>
                            {links.map(link => (
                                <li key={link.key}>
                                    <a
                                        href={link.href}
                                        target={link.external ? '_blank' : undefined}
                                        rel={link.external ? 'noopener noreferrer' : undefined}
                                        className='group inline-flex items-center gap-3 text-lg font-semibold underline-offset-4 hover:underline'
                                    >
                                        <span className='flex size-10 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-300 group-hover:-rotate-12'>
                                            <LinkIcon type={link.key} className='size-5' />
                                        </span>

                                        {link.cms ? <span data-mdlfy-cms={link.cms}>{link.label}</span> : link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className='lg:col-span-6'>
                    <Reveal y={60} delay={0.1}>
                        <ContactForm />
                    </Reveal>
                </div>
            </Container>
        </section>
    )
}
